import {
  COSMETICS, COVENANTS, PRISM_PACKS, RARITY_COLORS, SLOT_NAMES, TOWER_BY_ID, COSMETIC_BY_ID,
  type Cosmetic, type CosmeticSlot,
} from '@cs/content';
import type { Profile } from '@cs/protocol';
import type { AppContext } from '../../core/app';
import { defineScreen, go, markStoreSeen, profileOf, timeLeft, today, topBar } from '../shell';
import { tapSafe, errorToast, fmt, h, haptic, hex, modal, toast } from '../dom';
import { checkIcon, lockIcon, prismIcon, shardIcon } from '../icons';
import { cosmeticDrawer, crystalDrawer, previewCanvas, towerDrawer, type Drawer } from '../previews';
import { covenantCard, openCovenant, ownsCovenant } from './covenants';
import { dailySetPieceCard, openSet, setHeroSlide, setShop } from './skinsets';
import { accessibility } from '../../core/accessibility';

type Tab = 'featured' | 'sets' | 'covenants' | CosmeticSlot | 'prisms';
const SLOT_GLYPH: Record<CosmeticSlot, string> = { towerSkin: '⚒', towerEffect: '✷', crystalSkin: '◈', crystalEffect: '✺', killEffect: '☄' };
const TABS: { id: Tab; label: string; glyph: string }[] = [
  { id: 'featured', label: 'Featured', glyph: '✦' },
  { id: 'sets', label: 'Skin Sets', glyph: '❖' },
  { id: 'covenants', label: 'Covenants', glyph: '⚔' },
  ...(Object.keys(SLOT_NAMES) as CosmeticSlot[]).map((s) => ({ id: s as Tab, label: SLOT_NAMES[s], glyph: SLOT_GLYPH[s] })),
  { id: 'prisms', label: 'Prisms', glyph: '◆' },
];

const RARITY_ORDER = ['common', 'rare', 'epic', 'legendary', 'mythic'];
const rank = (c: Cosmetic) => RARITY_ORDER.indexOf(c.rarity);
export const forSale = (c: Cosmetic) => !c.exclusive && (c.price.shards !== undefined || c.price.prisms !== undefined);

export function slotLabel(c: Cosmetic): string {
  if (c.slot === 'towerSkin') return c.tower === 'any' ? 'Tower Skin · All towers' : `Tower Skin · ${TOWER_BY_ID[c.tower]?.name ?? c.tower}`;
  return SLOT_NAMES[c.slot].replace(/s$/, '');
}

export function priceTag(c: Cosmetic): HTMLElement {
  return h('div', { class: 'price' },
    c.price.shards !== undefined ? h('span', { class: 'p-shards' }, shardIcon(), fmt(c.price.shards)) : null,
    c.price.shards !== undefined && c.price.prisms !== undefined ? h('span', { class: 'p-or' }, 'or') : null,
    c.price.prisms !== undefined ? h('span', { class: 'p-prisms' }, prismIcon(), fmt(c.price.prisms)) : null,
  );
}

function isEquipped(p: Profile, c: Cosmetic): boolean {
  if (c.slot === 'towerSkin') return p.equipped.towerSkins[c.tower] === c.id;
  return (p.equipped as unknown as Record<string, string>)[c.slot] === c.id;
}

/** Seeded daily rotation: the same offers for everyone on a given UTC day, no server needed. */
function rotation(day: number): Cosmetic[] {
  const pool = COSMETICS.filter(forSale);
  let s = (day * 2654435761) >>> 0;
  const rnd = () => { s = (Math.imul(s ^ (s >>> 15), 2246822519) + 0x9e3779b9) >>> 0; return s / 4294967296; };
  const a = [...pool];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  // Heroes: the three rarest of today's shuffle; offers: the next four.
  const showy = (c: Cosmetic) => (c.slot === 'towerSkin' || c.slot === 'crystalSkin' ? 2 : c.slot === 'crystalEffect' ? 1 : 0);
  const heroes = [...a].sort((x, y) => showy(y) - showy(x) || rank(y) - rank(x)).slice(0, 3);
  const offers = a.filter((c) => !heroes.includes(c)).slice(0, 4);
  return [...heroes, ...offers];
}
export const featuredItems = () => rotation(today()).slice(0, 3);
export const dailyOffers = () => rotation(today()).slice(3, 7);
const refreshAt = () => new Date((today() + 1) * 86400000).toISOString();

/** A rarity-framed cosmetic card with a live preview. */
export function itemCard(c: Cosmetic, opts: { owned: boolean; equipped?: boolean; size?: number; onClick?: () => void; showPrice?: boolean; ribbon?: string }): HTMLElement {
  const size = opts.size ?? 168;
  return h('button', {
    class: `card clickable item-card rarity-${c.rarity}${opts.owned ? ' owned' : ''}${opts.equipped ? ' equipped' : ''}`,
    style: { '--rc': hex(RARITY_COLORS[c.rarity]) } as never,
    onclick: opts.onClick,
  },
    opts.ribbon ? h('span', { class: 'ic-ribbon' }, opts.ribbon) : null,
    h('div', { class: 'ic-preview' }, previewCanvas(cosmeticDrawer(c), size, size * 0.8)),
    h('div', { class: 'ic-meta' },
      h('div', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), c.rarity),
      h('div', { class: 'ic-name' }, c.name),
      h('div', { class: 'ic-slot' }, slotLabel(c)),
    ),
    h('div', { class: 'ic-foot' },
      opts.equipped ? h('div', { class: 'ic-badge eq' }, 'Equipped')
        : opts.owned ? h('div', { class: 'ic-badge' }, checkIcon(), 'Owned')
        : opts.showPrice !== false ? priceTag(c) : null),
  );
}

/** Default (un-skinned) look for the try-on toggle. */
function baseDrawer(c: Cosmetic): Drawer | null {
  if (c.slot === 'towerSkin') return towerDrawer(c.tower === 'any' ? 'bolt' : c.tower, undefined, 2);
  if (c.slot === 'crystalSkin') { const d = COSMETIC_BY_ID['crystal_default']; return d && d.slot === 'crystalSkin' ? crystalDrawer(d.recipe) : null; }
  return null;
}

/** Item detail sheet: big live preview, try-on toggle, prices in both currencies, equip. */
export async function openItem(ctx: AppContext, c: Cosmetic): Promise<void> {
  // Skin set bundles and pieces open the set sheet (try-on on any tower, set pricing).
  if (c.slot === 'towerSkin' && c.set) return openSet(ctx, c.set, c.tower === 'any' ? undefined : c.tower);
  let closeDetail: ((ok: boolean) => void) | undefined;
  const p = profileOf(ctx);
  const owned = p.inventory.includes(c.id);
  const equipped = owned && isEquipped(p, c);
  const big = Math.min(420, Math.round(innerWidth * 0.86));
  const stage = h('div', { class: 'id-stage' }, previewCanvas(cosmeticDrawer(c), big, Math.round(big * 0.78)));
  const base = baseDrawer(c);
  let tryOn = true;
  const toggle = base ? h('button', { class: 'tryon on', 'data-sfx': 'click', onclick: () => {
    tryOn = !tryOn;
    toggle!.classList.toggle('on', tryOn);
    toggle!.lastChild!.textContent = tryOn ? 'Wearing it' : 'Without it';
    stage.replaceChildren(previewCanvas(tryOn ? cosmeticDrawer(c) : base, big, Math.round(big * 0.78)));
  } }, h('span', { class: 'tryon-knob' }), h('span', null, 'Wearing it')) : null;

  const buyBtn = (cur: 'shards' | 'prisms') => {
    const price = c.price[cur];
    if (price === undefined) return null;
    const bal = cur === 'shards' ? p.shards : p.prisms;
    const afford = bal >= price;
    return h('button', {
      class: `btn big buy-btn ${cur === 'prisms' ? 'arcane' : 'primary'}`, disabled: !afford, 'data-sfx': 'confirm',
      onclick: async () => {
        const ok = await modal({
          title: `Purchase ${c.name}?`,
          body: h('div', { class: 'confirm-price' }, cur === 'shards' ? shardIcon('ico lg') : prismIcon('ico lg'), h('b', null, fmt(price)), ` ${cur === 'shards' ? 'Shards' : 'Prisms'}`),
          confirm: 'Buy',
          onConfirm: () => ctx.api.buy(c.id, cur),
        });
        if (ok) { ctx.audio.ui('purchase'); closeDetail?.(false); celebrate(c); }
      },
    }, cur === 'shards' ? shardIcon() : prismIcon(), afford ? fmt(price) : `Need ${fmt(price - bal)}`);
  };

  const actions = owned
    ? (equipped ? h('div', { class: 'ic-badge eq big' }, checkIcon(), 'Equipped')
      : h('button', { class: 'btn primary big', 'data-sfx': 'equip', onclick: async () => {
        try {
          await ctx.api.equip(c.slot, c.id, c.slot === 'towerSkin' ? c.tower : undefined);
          toast(`${c.name} equipped`, 'good'); closeDetail?.(false);
        } catch (e) { errorToast(e); }
      } }, 'Equip'))
    : c.exclusive ? h('div', { class: 'muted' }, c.earn ? `Exclusive - ${c.earn}` : 'Exclusive - earned through the Battle Pass.')
    : h('div', { class: 'id-buy' }, buyBtn('shards'), buyBtn('prisms'));

  await modal({
    title: c.name,
    wide: true,
    onOpen: (close) => { closeDetail = close; },
    cancel: 'Close',
    body: h('div', { class: `item-detail rarity-${c.rarity}`, style: { '--rc': hex(RARITY_COLORS[c.rarity]) } as never },
      h('div', { class: 'id-preview' }, stage, toggle),
      h('div', { class: 'id-info' },
        h('div', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), c.rarity),
        h('div', { class: 'id-slot' }, slotLabel(c)),
        h('p', { class: 'id-desc' }, c.desc),
        owned && !equipped ? h('div', { class: 'ic-badge' }, checkIcon(), 'In your locker') : null,
        actions,
      ),
    ),
  });
}

/** Full-screen reveal when a new item is unlocked. */
export function celebrate(c: Cosmetic): void {
  haptic('success');
  const sparks = h('div', { class: 'ub-sparks' }, Array.from({ length: 18 }, (_, i) => h('span', { style: { '--a': `${i * 20}deg`, '--d': `${0.6 + (i % 5) * 0.12}s` } as never })));
  const el = h('div', { class: `unlock-burst rarity-${c.rarity}`, style: { '--rc': hex(RARITY_COLORS[c.rarity]) } as never },
    h('div', { class: 'ub-rays' }), sparks,
    h('div', { class: 'ub-card' },
      h('div', { class: 'ub-eyebrow' }, 'Unlocked!'),
      previewCanvas(cosmeticDrawer(c), 260, 220),
      h('div', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), c.rarity), h('div', { class: 'ub-name' }, c.name), h('div', { class: 'muted' }, 'Added to your locker · tap to continue')));
  const dismiss = () => { el.classList.add('out'); setTimeout(() => el.remove(), 300); };
  el.addEventListener('click', dismiss);
  document.getElementById('app')!.append(el);
  setTimeout(dismiss, accessibility.messageMs(3400));
}

export const storeScreen = defineScreen<{ tab?: Tab } | undefined>((ctx, params, scope) => {
  let tab: Tab = params?.tab ?? 'featured';
  markStoreSeen();
  const chips = TABS.map((t) => h('button', {
    class: `store-chip${t.id === 'prisms' ? ' chip-prisms' : ''}`, 'data-sfx': 'tabSwitch',
    onclick: () => { if (tab === t.id) return; tab = t.id; paintChips(); render(profileOf(ctx)); scroller.scrollTop = 0; },
  }, h('span', { class: 'chip-glyph' }, t.glyph), t.label));
  const paintChips = () => chips.forEach((c, i) => {
    const on = TABS[i]!.id === tab;
    c.classList.toggle('active', on);
    if (on) c.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  });
  const body = h('div', { class: 'store-body' });
  const scroller = h('div', { class: 'scroll store-scroll' }, body);
  // Live countdown (text patch only).
  const timers: HTMLElement[] = [];
  scope.interval(() => { const s = timeLeft(refreshAt()); for (const t of timers) t.textContent = s; }, 30000);

  const render = (p: Profile) => {
    timers.length = 0;
    if (tab === 'prisms') { body.replaceChildren(prismPacks(ctx)); return; }
    if (tab === 'covenants') { body.replaceChildren(covenantShop(ctx, p)); return; }
    if (tab === 'sets') { body.replaceChildren(setShop(ctx, p)); return; }
    if (tab === 'featured') { body.replaceChildren(featured(ctx, p, scope, timers)); return; }
    const items = COSMETICS.filter((c) => c.slot === tab && forSale(c)).sort((a, b) => rank(b) - rank(a));
    body.replaceChildren(
      h('div', { class: 'section-head' }, h('h2', null, SLOT_NAMES[tab as CosmeticSlot]), h('span', { class: 'muted' }, `${items.length} items`)),
      h('div', { class: 'item-grid' }, items.map((c) => itemCard(c, { owned: p.inventory.includes(c.id), equipped: isEquipped(p, c), onClick: () => void openItem(ctx, c) }))),
    );
  };

  paintChips();
  render(profileOf(ctx));
  scope.add(ctx.api.subscribe(tapSafe(render)));

  return h('div', { class: 'screen-col store' },
    topBar(ctx, scope, { title: 'Store' }),
    h('div', { class: 'store-chips' }, chips),
    scroller,
  );
}, { bg: { x: 0.5, dim: 0.3 }, online: true });

function featured(ctx: AppContext, p: Profile, scope: { interval: (fn: () => void, ms: number) => void }, timers: HTMLElement[]): HTMLElement {
  const heroes = featuredItems();
  const reduced = accessibility.reducedMotion;
  const slides = heroes.map((c) => {
    const owned = p.inventory.includes(c.id);
    return h('button', { class: `hero-slide rarity-${c.rarity}`, style: { '--rc': hex(RARITY_COLORS[c.rarity]) } as never, onclick: () => void openItem(ctx, c) },
      h('span', { class: 'hs-glow' }),
      h('span', { class: 'hs-preview' }, previewCanvas(cosmeticDrawer(c), 360, 280, 'preview', !reduced)),
      h('span', { class: 'hs-info' },
        h('span', { class: 'hs-eyebrow' }, 'Featured'),
        h('span', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), c.rarity),
        h('span', { class: 'hs-name' }, c.name),
        h('span', { class: 'hs-slot' }, slotLabel(c)),
        owned ? h('span', { class: 'ic-badge big' }, checkIcon(), 'Owned') : priceTag(c),
      ),
    );
  });
  // The skin set of the day leads the Featured shelf.
  slides.unshift(setHeroSlide(ctx, p, !reduced));
  const track = h('div', { class: 'hero-track' }, slides);
  const dots = h('div', { class: 'banner-dots' }, slides.map((_, i) => h('span', { class: 'banner-dot' + (i === 0 ? ' on' : '') })));
  let idx = 0, touched = 0;
  track.addEventListener('pointerdown', () => { touched = performance.now(); });
  track.addEventListener('scroll', () => {
    const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    if (i !== idx) { idx = i; [...dots.children].forEach((d, k) => d.classList.toggle('on', k === idx)); }
  }, { passive: true });
  if (!reduced) scope.interval(() => {
    if (!track.isConnected || performance.now() - touched < 8000) return;
    idx = (idx + 1) % slides.length;
    track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
    [...dots.children].forEach((d, k) => d.classList.toggle('on', k === idx));
  }, 5500);

  const timer = h('b', { class: 'countdown' }, timeLeft(refreshAt()));
  timers.push(timer);
  const offers = dailyOffers();
  const locked = COVENANTS.filter((c) => c.tier === 'advanced' && !ownsCovenant(p, c.id));
  const best = PRISM_PACKS[PRISM_PACKS.length - 1]!;

  return h('div', { class: 'featured-page' },
    h('section', { class: 'hero-carousel' }, track, dots),
    h('div', { class: 'section-head' },
      h('h2', null, 'Daily Offers'),
      h('span', { class: 'refresh' }, h('span', { class: 'clock' }, '⧗'), 'New in ', timer),
    ),
    h('div', { class: 'item-grid offers' }, offers.slice(0, 3).map((c) => itemCard(c, { owned: p.inventory.includes(c.id), equipped: isEquipped(p, c), ribbon: 'Daily', onClick: () => void openItem(ctx, c) })), dailySetPieceCard(ctx, p)),
    locked.length ? h('div', { class: 'section-head' }, h('h2', null, 'Covenant Spotlight'), h('button', { class: 'btn link', onclick: () => go(ctx, 'store', { tab: 'covenants' }) }, 'See all')) : null,
    locked.length ? h('div', { class: 'cov-spot' }, covenantCard(p, locked[0]!, { onClick: () => void openCovenant(ctx, locked[0]!.id) })) : null,
    h('button', { class: 'prism-promo', 'data-sfx': 'click', onclick: () => go(ctx, 'store', { tab: 'prisms' }) },
      h('span', { class: 'pp-gems' }, prismIcon('ico gem g0'), prismIcon('ico gem g1'), prismIcon('ico gem g2')),
      h('span', { class: 'pp-text' }, h('b', null, 'Need Prisms?'), h('small', null, `Up to ${fmt(best.prisms + best.bonus)} Prisms in a single pack`)),
      h('span', { class: 'pp-cta' }, 'Shop'),
    ),
  );
}

function prismPacks(ctx: AppContext): HTMLElement {
  const buy = async (sku: string, name: string) => {
    const status = h('div', { class: 'checkout-status' }, h('div', { class: 'spinner' }), h('span', null, 'Opening secure checkout...'));
    let finished = false;
    void modal({ title: name, body: status, cancel: 'Close' }).then(() => { finished = true; });
    try {
      const res = await ctx.api.checkout(sku);
      if (res.url) window.open(res.url, '_blank', 'noopener');
      let r = res;
      for (let i = 0; i < 60 && r.status === 'pending' && !finished; i++) {
        status.lastChild!.textContent = 'Waiting for payment confirmation...';
        await new Promise((ok) => setTimeout(ok, 1500));
        r = await ctx.api.checkoutStatus(res.checkoutId);
      }
      if (r.status === 'completed') {
        await ctx.api.refresh();
        status.replaceChildren(prismIcon('ico xl'), h('div', null, h('b', null, 'Payment complete!'), h('div', { class: 'muted' }, 'Your Prisms have been added.')));
        ctx.audio.ui('purchase');
        haptic('success');
        toast('Prisms added to your wallet', 'gold');
      } else if (r.status === 'failed') {
        status.replaceChildren(h('b', { class: 'bad' }, 'Payment failed or was cancelled.'));
      }
    } catch (e) { errorToast(e); status.replaceChildren(h('b', { class: 'bad' }, 'Checkout could not be started.')); }
  };
  const ribbons = ['', 'Popular', '', 'Best value'];
  return h('div', { class: 'prism-store' },
    h('div', { class: 'section-head' }, h('h2', null, 'Prism Packs'), h('span', { class: 'muted' }, 'Premium currency')),
    h('div', { class: 'pack-grid' }, PRISM_PACKS.map((pk, i) => h('div', { class: `pack-card card tier-${i}` },
      ribbons[i] ? h('span', { class: 'pack-ribbon' }, ribbons[i]) : null,
      h('div', { class: 'pack-gems' }, Array.from({ length: i + 1 }, (_, k) => prismIcon(`ico gem g${k}`))),
      h('div', { class: 'pack-name' }, pk.name),
      h('div', { class: 'pack-amt' }, prismIcon(), fmt(pk.prisms)),
      pk.bonus ? h('div', { class: 'pack-bonus' }, `+${fmt(pk.bonus)} bonus`) : h('div', { class: 'pack-bonus none' }, 'Base pack'),
      h('button', { class: 'btn primary', 'data-sfx': 'confirm', onclick: () => void buy(pk.sku, pk.name) }, `$${pk.priceUsd.toFixed(2)}`),
    ))),
    h('div', { class: 'test-mode-note' }, lockIcon(), h('span', null, h('b', null, 'Test mode. '), 'No real money is charged - purchases complete instantly through the test payment provider.')),
  );
}

/** Advanced Covenants for sale as premium cards. */
function covenantShop(ctx: AppContext, p: Profile): HTMLElement {
  const advanced = COVENANTS.filter((c) => c.tier === 'advanced');
  const lockedCount = advanced.filter((c) => !ownsCovenant(p, c.id)).length;
  return h('div', { class: 'cov-shop' },
    h('div', { class: 'cov-shop-intro' },
      h('h2', null, 'Advanced Covenants'),
      h('p', { class: 'muted' }, lockedCount
        ? 'New orders with their own themes, passives and six unique towers. Unlock them with Shards earned in matches, or with Prisms.'
        : 'You command every Covenant. The realm trembles.'),
    ),
    h('div', { class: 'cov-shop-grid' }, advanced.map((c) => covenantCard(p, c, { onClick: () => void openCovenant(ctx, c.id) }))),
  );
}
